
import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { MappedLeadData } from '../types';
import { getAppwriteStorageDownloadUrl, getInitials, getICPConfig, getEffectiveUserId } from '../utils/helpers';
import { useAuth } from '../contexts/AuthContext';
import { n8nService, isInsufficientCreditsError, formatInsufficientCreditsMessage } from './core/services/n8n.service';
import { getStatusConfig, getStatusLabel, getNormalizedStatusKey, getTransitionalStatusLabel, getLeadSubstatusDisplay } from '../utils/statusMapping';
import { queryKeys } from '../services/query/queryKeys';
import { appwriteQueryFns } from '../services/query/appwriteQueryFns';
import { showSuccessToast, showErrorToast, showInfoToast, showInsufficientCreditsToast } from '../utils/toast';
import { openBuyCredits } from '../stores/uiStore';
import { useLeadPolling } from '../hooks/useLeadPolling';
import { fetchCampaignMetadata } from '../utils/data';
import { canLeadHaveReport, getLeadReportFileId, getLeadReportStoragePath, getLeadReportUrl } from '../utils/leadReports';
import { useClientProfile } from '../hooks/useClientProfile';
import { useCampaigns } from '../hooks/useCampaigns';
import SupportRequestModal from './modals/SupportRequestModal';
import { useTeamPreferences } from '../hooks/useTeamPreferences';

interface SidebarProps {
  data: MappedLeadData;
  previewMode?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ data, previewMode = false }) => {
  "use memo";
  const icpConfig = getICPConfig(data.icpScore);
  const { currentUser, targetEmail: impersonatedEmail } = useAuth();
  // Use impersonated email if set (admin viewing as another user), otherwise current user's email
  const effectiveUserEmail = impersonatedEmail || currentUser?.email;
  const queryClient = useQueryClient();
  const [profileImageError, setProfileImageError] = useState(false);
  const [isEnriching, setIsEnriching] = useState(false);
  const [isPollingEnrich, setIsPollingEnrich] = useState(false);
  const [isOutreaching, setIsOutreaching] = useState(false);
  const [optimisticStatus, setOptimisticStatus] = useState<string | null>(null);
  const [showUnlockOutreachModal, setShowUnlockOutreachModal] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);

  const isTruthyFlag = (value: unknown): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }
    return false;
  };

  // Client profile state for impersonation support
  const [clientProfile, setClientProfile] = useState<{ client_id: string } | null>(null);

  // CRITICAL FIX: Fetch lead data once on mount, NOT repeatedly
  // The useLeadPolling hook handles status updates during enriching/outreaching
  // This query only fetches initial data - polling handles updates during actions
  const { data: cachedLeadData } = useQuery({
    queryKey: queryKeys.leads.detail(data.campaignId, data.id.toString()),
    queryFn: async () => {
      const result = await appwriteQueryFns.leads.detail({
        campaignId: data.campaignId,
        leadId: data.id.toString(),
        userEmail: effectiveUserEmail || '',
      });
      // Store client profile for impersonation support
      if (result?.client_id) {
        setClientProfile({ client_id: result.client_id });
      }
      return result;
    },
    // Show prop data immediately while initial fetch runs
    placeholderData: data,
    // NEVER refetch on mount - only fetch once when component mounts
    refetchOnMount: false,
    // Don't refetch when window regains focus
    refetchOnWindowFocus: false,
    // staleTime: 60 seconds prevents constant refetching
    // The useLeadPolling hook handles status updates during enrichment
    staleTime: 60 * 1000,
    // Only run when we have user email
    enabled: !!effectiveUserEmail && !previewMode,
  });

  // Resolve campaign metadata from the cached useCampaigns() list (single source of
  // truth, keyed by the effective/impersonated email) and fall back to a direct
  // fetch only when it isn't cached. Mirrors LeadReportModal — prevents the stale
  // per-lead campaign_metrics re-fetch that previously timed out and dropped the
  // campaign object under impersonation.
  const campaignIdForLookup = (data.campaignId || '').trim().toLowerCase() === 'unknown'
    ? ''
    : data.campaignId;
  const { data: cachedCampaigns } = useCampaigns(
    previewMode ? '' : (effectiveUserEmail || undefined)
  );
  const cachedCampaign = cachedCampaigns?.find(
    (c) => c.id === campaignIdForLookup || (c.id && campaignIdForLookup && c.id.toLowerCase() === campaignIdForLookup.toLowerCase())
  );
  const { data: campaignMetadata } = useQuery({
    queryKey: ['campaign-metadata', campaignIdForLookup, effectiveUserEmail],
    queryFn: () => fetchCampaignMetadata(campaignIdForLookup),
    staleTime: 60 * 1000,
    enabled: !!effectiveUserEmail && !!campaignIdForLookup && !previewMode && !cachedCampaign,
  });

  const resolvedCampaignMetadata = cachedCampaign || campaignMetadata;

  const { data: clientInfo } = useClientProfile(effectiveUserEmail || undefined);
  // Module gates come from the Appwrite TEAM preferences (mod_* flags) — the
  // single source of truth shared with Settings → Modules. The client-profile
  // flag stays as a legacy fallback while older profiles lack team prefs.
  const { data: teamPrefs } = useTeamPreferences();
  const hasSalesModule = Boolean(teamPrefs?.modules?.sales);
  const hasOutreachModule = Boolean(teamPrefs?.modules?.outreach)
    || Boolean(clientInfo?.hasOutreachModule);

  const isCampaignOutreachBlocked = isTruthyFlag(resolvedCampaignMetadata?.block_outreach);

  // Priority: optimisticStatus (during action) > cached status (synced with grid) > prop status (fallback)
  // Using ?? instead of || to only fallback on null/undefined, not on falsy values like empty string
  const currentStatus = optimisticStatus ?? cachedLeadData?.status ?? data.status;
  const currentStatusLabel = getStatusLabel(currentStatus);
  // DB substatus (e.g. "not started", "Ready to Outreach") — shown in the
  // status pill when the lead carries one; transitional statuses
  // ("Writing message…") arrive AS the status and resolve via their own label.
  const currentSubstatus = (cachedLeadData as MappedLeadData | undefined)?.salesSubstatus
    ?? data.salesSubstatus ?? null;
  const transitionalLabel = getTransitionalStatusLabel(currentStatus);
  // OUTREACHING with no substatus defaults to "Not started".
  const substatusPillLabel = transitionalLabel
    ?? getLeadSubstatusDisplay(currentStatus, currentSubstatus);
  const reportLead = {
    ...data,
    status: currentStatus,
  };
  const reportFileId = getLeadReportFileId(reportLead);
  const reportStoragePath = getLeadReportStoragePath(reportLead);
  // Fast path: lvl2.report_lead_url proves the report exists — skip the probe.
  const storedReportUrl = getLeadReportUrl(reportLead);
  const reportDownloadUrl = storedReportUrl
    ?? (reportFileId ? getAppwriteStorageDownloadUrl('lead-reports', String(reportFileId)) : null);
  const shouldCheckReport = !previewMode && canLeadHaveReport(currentStatus)
    && Boolean(reportStoragePath) && !storedReportUrl;
  const { data: reportExists, isLoading: isCheckingReport } = useQuery({
    queryKey: queryKeys.storage.report(reportStoragePath || 'missing'),
    queryFn: () => appwriteQueryFns.storage.reportExists(reportStoragePath!),
    enabled: shouldCheckReport,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const canDownloadReport = Boolean(reportDownloadUrl) && reportExists === true;
  const reportMissingStatuses = ['ENRICHED', 'OUTREACHING', 'OUTREACHED', 'ENGAGED', 'PROPOSAL', 'CONVERTED'] as const;
  const shouldShowRetryEnrich =
    !previewMode &&
    (reportMissingStatuses as readonly string[]).includes(currentStatusLabel) &&
    shouldCheckReport &&
    reportExists === false &&
    !isCheckingReport &&
    !isEnriching;

  // Background polling for enrichment status
  useLeadPolling({
    campaignId: data.campaignId,
    leadId: data.id.toString(),
    userEmail: effectiveUserEmail,
    enabled: isPollingEnrich && !previewMode,
    onStatusChange: (newStatus) => {
      const statusLabel = getStatusLabel(newStatus);

      // Show completion toast based on status
      if (statusLabel === 'ENRICHED') {
        showSuccessToast(`✓ ${data.leadName} enriched successfully!`);
        setIsEnriching(false);
        setIsPollingEnrich(false);
        setOptimisticStatus(null);
        queryClient.invalidateQueries({ queryKey: ['storage', 'report'] });
        queryClient.invalidateQueries({ queryKey: ['storage', 'campaignReports', data.campaignId] });
      } else if (statusLabel === 'ENRICHING') {
        // Intermediate state, keep polling
      }
    },
    shouldStopPolling: (newStatus) => getStatusLabel(newStatus) === 'ENRICHED',
    onTimeout: () => {
      // Polling timed out - clear optimistic status and refetch actual status from N8N
      console.warn('⏱️ [ENRICH] Polling timed out, clearing optimistic status');
      setIsEnriching(false);
      setIsPollingEnrich(false);
      setOptimisticStatus(null);
      showInfoToast(`Enrichment is still in progress for ${data.leadName}. You can retry if it appears stuck.`);

      // Invalidate both caches to get actual status from N8N
      queryClient.invalidateQueries({
        queryKey: queryKeys.leads.detail(data.campaignId, data.id.toString()),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.leads.byCampaign(data.campaignId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.leads.byCampaign(data.campaignId, effectiveUserEmail),
      });
      queryClient.invalidateQueries({ queryKey: ['storage', 'report'] });
      queryClient.invalidateQueries({ queryKey: ['storage', 'campaignReports', data.campaignId] });
    },
    pollingInterval: 10000, // Poll every 10 seconds — avoids excessive API calls
    maxPollingDuration: 5 * 60 * 1000, // 5 minutes max — cache invalidation handles the rest
  });

  // Background polling for outreach status
  useLeadPolling({
    campaignId: data.campaignId,
    leadId: data.id.toString(),
    userEmail: effectiveUserEmail,
    enabled: isOutreaching && !previewMode,
    onStatusChange: (newStatus) => {
      const statusLabel = getStatusLabel(newStatus);

      if (statusLabel === 'OUTREACHED') {
        showSuccessToast(`✓ Outreach sent to ${data.leadName}!`);
        setIsOutreaching(false);
        setOptimisticStatus(null);
      } else if (statusLabel === 'OUTREACHING') {
        // Intermediate state, keep polling
      }
    },
    shouldStopPolling: (newStatus) => getStatusLabel(newStatus) === 'OUTREACHED',
    onTimeout: () => {
      // Polling timed out - clear optimistic status and refetch actual status from server
      console.warn('⏱️ [OUTREACH] Polling timed out, clearing optimistic status');
      setIsOutreaching(false);
      setOptimisticStatus(null);
      showInfoToast(`Outreach is still processing for ${data.leadName}. The lead will stay in the outreach flow until N8N finishes.`);

      // Invalidate both caches to get actual status from N8N
      queryClient.invalidateQueries({
        queryKey: queryKeys.leads.detail(data.campaignId, data.id.toString()),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.leads.byCampaign(data.campaignId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.leads.byCampaign(data.campaignId, effectiveUserEmail),
      });
    },
    pollingInterval: 5000, // Poll every 5 seconds (reduced from 3s)
    maxPollingDuration: 10 * 60 * 1000, // Outreach can legitimately stay in transition for up to 10 minutes
  });

  // Handle ENRICH button click - triggers N8N enrichment workflow
  const handleEnrich = async () => {
    if (!effectiveUserEmail) {
      showErrorToast('Please log in to enrich leads');
      return;
    }

    try {
      setIsEnriching(true);
      setIsPollingEnrich(true);
      setOptimisticStatus('ENRICHING'); // Optimistic update for modal

      // Optimistically update BOTH caches so modal AND grid show "ENRICHING" immediately
      // 1. Update detail cache (for modal/sidebar)
      const previousData = queryClient.getQueryData(
        queryKeys.leads.detail(data.campaignId, data.id.toString())
      );

      queryClient.setQueryData(
        queryKeys.leads.detail(data.campaignId, data.id.toString()),
        (old: any) => {
          if (!old) return old;
          return { ...old, status: 'ENRICHING' };
        }
      );

      // 2. Update grid cache (for campaign table) - CRITICAL FIX for cache sync
      queryClient.setQueryData(
        queryKeys.leads.byCampaign(data.campaignId),
        (oldData: any[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((lead: any) =>
            String(lead.id) === data.id.toString()
              ? { ...lead, status: 'ENRICHING' }
              : lead
          );
        }
      );
      queryClient.setQueryData(
        queryKeys.leads.byCampaign(data.campaignId, effectiveUserEmail),
        (oldData: any[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((lead: any) =>
            String(lead.id) === data.id.toString()
              ? { ...lead, status: 'ENRICHING' }
              : lead
          );
        }
      );

      const response = await n8nService.bulkEnrichLeads({
        campaign_id: data.campaignId.toString(),
        lead_ids: [data.id.toString()],
        client_email: effectiveUserEmail,
        client_id: getEffectiveUserId(impersonatedEmail, currentUser?.uid, clientProfile),
        client_name: currentUser.displayName || undefined,
        action: 'enrich'
      });

      showSuccessToast(`Enriching ${data.leadName}... Check back in a few seconds`);

      // Release the UI immediately - user can keep working while enrichment runs in background
      // Polling continues (isPollingEnrich) to detect completion and show the success toast
      setIsEnriching(false);

    } catch (error: any) {
      // Credit-checker rejection — surface the message + open Buy Credits modal.
      // Duck-type (not instanceof) so the CTA survives bundler class-identity splitting.
      if (isInsufficientCreditsError(error)) {
        console.warn('💳 [ENRICH] Insufficient credits:', error.answer);
        showInsufficientCreditsToast(formatInsufficientCreditsMessage(error.answer));
        openBuyCredits();
      } else {
        console.error('❌ [ENRICH] Failed:', error);
        showErrorToast('Failed to enrich lead. Please try again.');
      }

      // Revert optimistic updates on error
      setIsEnriching(false);
      setIsPollingEnrich(false);
      setOptimisticStatus(null);

      // Revert BOTH caches to original status
      // 1. Revert detail cache
      queryClient.setQueryData(
        queryKeys.leads.detail(data.campaignId, data.id.toString()),
        (old: any) => {
          if (!old) return old;
          return { ...old, status: data.status }; // Revert to original status
        }
      );

      // 2. Revert grid cache by invalidating (refetch from server)
      queryClient.invalidateQueries({
        queryKey: queryKeys.leads.byCampaign(data.campaignId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.leads.byCampaign(data.campaignId, effectiveUserEmail),
      });
    }
  };

  const handleDownloadReport = () => {
    if (!reportDownloadUrl || !canDownloadReport) {
      showErrorToast('Report file is not available for this lead yet.');
      return;
    }

    window.open(reportDownloadUrl, '_blank', 'noopener,noreferrer');
  };

  // Handle OUTREACH button click - triggers N8N outreach workflow
  const handleOutreach = async () => {
    if (!effectiveUserEmail) {
      showErrorToast('Please log in to send outreach');
      return;
    }

    if (!hasOutreachModule) {
      setShowUnlockOutreachModal(true);
      return;
    }

    if (isCampaignOutreachBlocked) {
      showInfoToast('Outreach is blocked for this campaign.');
      return;
    }

    try {
      setIsOutreaching(true);
      setOptimisticStatus('OUTREACHING'); // Optimistic update for modal

      // Optimistically update BOTH caches so modal AND grid show "OUTREACHING" immediately
      // 1. Update detail cache (for modal/sidebar)
      const previousData = queryClient.getQueryData(
        queryKeys.leads.detail(data.campaignId, data.id.toString())
      );

      queryClient.setQueryData(
        queryKeys.leads.detail(data.campaignId, data.id.toString()),
        (old: any) => {
          if (!old) return old;
          return { ...old, status: 'OUTREACHING' };
        }
      );

      // 2. Update grid cache (for campaign table) - CRITICAL FIX for cache sync
      queryClient.setQueryData(
        queryKeys.leads.byCampaign(data.campaignId),
        (oldData: any[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((lead: any) =>
            String(lead.id) === data.id.toString()
              ? { ...lead, status: 'OUTREACHING' }
              : lead
          );
        }
      );
      queryClient.setQueryData(
        queryKeys.leads.byCampaign(data.campaignId, effectiveUserEmail),
        (oldData: any[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((lead: any) =>
            String(lead.id) === data.id.toString()
              ? { ...lead, status: 'OUTREACHING' }
              : lead
          );
        }
      );

      const response = await n8nService.sendToOutreach({
        campaign_id: data.campaignId.toString(),
        lead_ids: [data.id.toString()],
        outreach_type: 'both', // Default to both email and LinkedIn
        client_email: effectiveUserEmail,
        client_id: getEffectiveUserId(impersonatedEmail, currentUser?.uid, clientProfile),
        client_name: currentUser.displayName || undefined,
        action: 'outreach'
      });

      showSuccessToast(`Sending outreach to ${data.leadName}...`);

      // Polling will handle status detection and completion toast

    } catch (error: any) {
      // Credit-checker rejection — surface the message + open Buy Credits modal.
      if (isInsufficientCreditsError(error)) {
        console.warn('💳 [OUTREACH] Insufficient credits:', error.answer);
        showInsufficientCreditsToast(formatInsufficientCreditsMessage(error.answer));
        openBuyCredits();
      } else {
        console.error('❌ [OUTREACH] Failed:', error);
        showErrorToast('Failed to send outreach. Please try again.');
      }

      // Revert optimistic updates on error
      setIsOutreaching(false);
      setOptimisticStatus(null);

      // Revert BOTH caches to original status
      // 1. Revert detail cache
      queryClient.setQueryData(
        queryKeys.leads.detail(data.campaignId, data.id.toString()),
        (old: any) => {
          if (!old) return old;
          return { ...old, status: data.status }; // Revert to original status
        }
      );

      // 2. Revert grid cache by invalidating (refetch from server)
      queryClient.invalidateQueries({
        queryKey: queryKeys.leads.byCampaign(data.campaignId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.leads.byCampaign(data.campaignId, effectiveUserEmail),
      });
    }
  };

  const insightCards = [
    {
        label: 'Experience',
        value: (() => {
            // Handle yearsExperience as either string or number
            const years = data.yearsExperience;

            // Check for various "unknown" variants
            if (!years ||
                years === 'Not specified' ||
                years === 'Unknown' ||
                years === 'unknown' ||
                (typeof years === 'string' && years.trim().toLowerCase() === 'unknown')) {
                return '-';
            }

            // Convert to string and trim if it's a string, or just convert if it's a number
            const yearsStr = typeof years === 'string' ? years.trim() : String(years);
            return yearsStr !== '' ? `${yearsStr} YEARS` : '-';
        })(),
        icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>,
        color: 'text-[#5260FF]' // Primary
    },
    {
        label: 'Followers',
        value: (() => {
            // Format network size categories to user-friendly ranges
            const networkSize = data.linkedinFollowers?.toString().toUpperCase().trim();

            const networkSizeMap: Record<string, string> = {
                'VERY_LARGE': '10,000+',
                'LARGE': '5,000+',
                'MEDIUM': '1,000+',
                'SMALL': '500+',
                'VERY_SMALL': '<500',
                'UNKNOWN': 'N/A',
                'NOT SPECIFIED': 'N/A',
                'N/A': 'N/A',
                '--': 'N/A'
            };

            // If it's already a number, return it formatted
            if (networkSize && !isNaN(Number(networkSize))) {
                return Number(networkSize).toLocaleString();
            }

            // Map category to range
            if (networkSize && networkSizeMap[networkSize]) {
                return networkSizeMap[networkSize];
            }

            // If no match, return the value as-is or N/A
            return networkSize || 'N/A';
        })(),
        icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path>,
        color: 'text-[#15FBFF]' // Secondary
    },
    {
        label: 'Career Stage',
        value: data.careerStage,
        icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>,
        color: 'text-[#7486FF]' // Primary Light
    },
    { 
        label: 'Professional Focus', 
        value: data.professionalFocus,
        icon: <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></>,
        color: 'text-[#4FCDCF]' // Secondary Light
    },
    { 
        label: 'Decision Power', 
        value: data.decisionAuthority,
        icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>,
        color: 'text-[#5260FF]' // Primary
    },
    { 
        label: 'Influence Level', 
        value: data.influenceLevel,
        icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>,
        color: 'text-[#15FBFF]' // Secondary
    }
  ];

  return (
    <div className="space-y-4">
        {/* Profile Card - Compact */}
        <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-[#5260FF]/5 via-transparent to-[#15FBFF]/5 rounded-2xl blur-xl"></div>

            <div className="profile-container relative aspect-square w-full rounded-2xl bg-[#121212] overflow-hidden group shadow-xl shadow-black/50">
                <a href={data.leadLinkedin !== '#' ? data.leadLinkedin : data.companyWebsite} target="_blank" rel="noreferrer" className="block h-full w-full relative">
                    {data.profilePicture && !profileImageError ? (
                        <>
                            <img
                                src={data.profilePicture}
                                alt={data.leadName}
                                className="w-full h-full object-cover filter grayscale contrast-110 brightness-110 transition-all duration-500 ease-out group-hover:grayscale-0 group-hover:contrast-100 group-hover:brightness-100 group-hover:scale-105"
                                onError={() => setProfileImageError(true)}
                            />
                            {/* ID Number Overlay */}
                            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black via-black/90 to-transparent py-2 flex items-center justify-center">
                                <span className="text-xs font-bold text-white tracking-wider">ID #{data.id}</span>
                            </div>
                        </>
                    ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-neutral-700 text-5xl font-light bg-[#1a1a1a] relative">
                            {getInitials(data.leadName)}
                            {/* ID Number for no image */}
                            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black via-black/90 to-transparent py-2 flex items-center justify-center">
                                <span className="text-xs font-bold text-gray-400 tracking-wider">ID #{data.id}</span>
                            </div>
                        </div>
                    )}

                    {/* Subtle Vignette Effect for Depth */}
                    <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black/20 pointer-events-none"></div>
                </a>
            </div>
        </div>

        {/* ICP Score Badge - Compact - Only show if score > 0 */}
        {data.icpScore > 0 && (
            <div className="relative group">
                <div className={`absolute inset-0 ${icpConfig.wrapperClass} blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300`}></div>
                <div className={`relative ${icpConfig.wrapperClass} rounded-xl p-2.5 flex items-center gap-2.5 transition-all duration-300`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${icpConfig.iconBgClass} shadow-inner`}>
                        <svg className={`w-4 h-4 ${icpConfig.iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d={icpConfig.iconPath}/>
                        </svg>
                    </div>
                    <div className="flex-1">
                        <div className={`text-[9px] uppercase tracking-[0.15em] font-bold ${icpConfig.labelClass} opacity-80`}>ICP Score</div>
                        <div className={`text-lg font-black leading-tight ${icpConfig.textClass}`}>{data.icpScore}%</div>
                    </div>
                </div>
            </div>
        )}

        <div className="space-y-2.5">
        {/* Profile Completeness Badge - Compact */}
        {data.profileCompleteness > 0 && (
            <div className="flex items-center gap-2 rounded-lg bg-[#121212] px-2 py-1.5 transition-colors">
                <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-white/5">
                    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                        <div className="text-[9px] uppercase tracking-wider font-semibold text-gray-500">Enrichment</div>
                        <div className="text-[11px] font-black text-gray-300">{data.profileCompleteness}%</div>
                    </div>
                    <div className="mt-0.5 h-1 w-full overflow-hidden rounded-full bg-white/5">
                        <div
                            className="h-full bg-gradient-to-r from-[#5260FF] to-[#7486FF] rounded-full transition-all duration-500"
                            style={{ width: `${data.profileCompleteness}%` }}
                        />
                    </div>
                </div>
            </div>
        )}

        {/* Status + Substatus pill — primary umbrella status above, supporting
            substatus below. Renders only the status when no substatus exists. */}
        {(() => {
            const statusConfig = getStatusConfig(currentStatus);
            return (
                <div className={`flex shrink-0 flex-col rounded-lg border px-2 py-1.5 ${statusConfig.tailwindBg} ${statusConfig.tailwindBorder}`}>
                    <span className={`whitespace-nowrap text-[11px] font-black uppercase leading-none tracking-[0.08em] ${statusConfig.tailwindColor}`}>
                        {currentStatusLabel}
                    </span>
                    {substatusPillLabel && (
                        <span className={`mt-1 whitespace-nowrap text-[9px] font-semibold uppercase leading-none tracking-[0.06em] opacity-60 ${statusConfig.tailwindColor}`}>
                            {substatusPillLabel}
                        </span>
                    )}
                </div>
            );
        })()}

        {/* Action Button - Based on Enrichment Status */}
        <div className="relative group">
            {(() => {
                if (previewMode) {
                    return (
                        <div className="w-full rounded-lg border border-amber-400/20 bg-amber-400/10 px-3 py-2 text-center">
                            <div className="text-[9px] font-bold uppercase tracking-[0.16em] text-amber-200">Demo Preview</div>
                            <div className="mt-0.5 text-[11px] font-semibold leading-4 text-amber-50">Lead actions are disabled in the sample experience.</div>
                        </div>
                    );
                }
                // Use currentStatus which reads from cache for sync with grid view
                const statusConfig = getStatusConfig(currentStatus);
                const statusLabel = currentStatusLabel;

                if (statusLabel === 'NEW LEAD') {
                    return (
                        <button
                            className="flex min-h-9 w-full items-center justify-center gap-2 rounded-lg border border-white/10 bg-gradient-to-r from-[#5260FF] to-[#7486FF] px-3 py-2 font-bold text-white shadow-lg transition-all duration-300 hover:scale-[1.02] hover:from-[#7486FF] hover:to-[#5260FF] hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
                            onClick={handleEnrich}
                            disabled={isEnriching}
                        >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                            </svg>
                            <span className="text-[11px] uppercase tracking-wider">Enrich Lead</span>
                        </button>
                    );
                } else if (statusLabel === 'ENRICHING') {
                    // Only lock the button while this session is actively polling enrichment.
                    // If a lead remains ENRICHING later, let the user manually retry.
                    if (!isPollingEnrich) {
                        return (
                            <button
                                className="flex min-h-9 w-full items-center justify-center gap-2 rounded-lg border border-amber-400/30 bg-[linear-gradient(145deg,rgba(251,191,36,0.16),rgba(255,255,255,0.03))] px-3 py-2 font-bold text-amber-100 transition-all duration-300 hover:scale-[1.02] hover:border-amber-300/45 hover:bg-[linear-gradient(145deg,rgba(251,191,36,0.24),rgba(255,255,255,0.05))] hover:text-white hover:shadow-[0_18px_36px_rgba(245,158,11,0.16)]"
                                onClick={handleEnrich}
                            >
                                <Icon icon="bx:refresh" className="h-4 w-4 shrink-0" aria-hidden="true" />
                                <span className="text-[11px] uppercase tracking-wider">Retry Enrich</span>
                            </button>
                        );
                    }

                    // ENRICHING status - yellow/amber color, disabled, with spinner
                    return (
                        <button
                            className="flex min-h-9 w-full cursor-not-allowed items-center justify-center gap-2 rounded-lg border border-[#FBBF24]/30 bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] px-3 py-2 font-bold text-white shadow-lg shadow-yellow-500/20"
                            disabled
                        >
                            <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="text-[11px] uppercase tracking-wider">Enriching...</span>
                        </button>
                    );
                } else if (statusLabel === 'ENRICHED') {
                    if (shouldShowRetryEnrich) {
      return (
                            <div className="group/retry-enrich relative">
                                <button
                                    className="flex min-h-9 w-full items-center justify-center gap-2 rounded-lg border border-white/10 bg-gradient-to-r from-amber-300 to-yellow-200 px-3 py-2 font-bold text-[#0a0a0a] shadow-lg transition-all duration-300 hover:scale-[1.02] hover:from-amber-200 hover:to-yellow-100 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
                                    onClick={handleEnrich}
                                    disabled={isEnriching}
                                    aria-label="Enrich Again. The report was not generated. Run enrichment again to rebuild it."
                                >
                                    <Icon icon="solar:restart-bold" className="h-4 w-4 shrink-0" aria-hidden="true" />
                                    <span className="text-[11px] uppercase tracking-wider">Enrich Again</span>
                                </button>
                                <div className="pointer-events-none absolute bottom-full left-1/2 z-40 mb-2 hidden w-64 -translate-x-1/2 rounded-2xl border border-white/10 bg-[#0B0B0D]/96 p-3 text-xs leading-5 text-gray-200 shadow-[0_20px_60px_rgba(0,0,0,0.45)] backdrop-blur-xl group-hover/retry-enrich:block group-focus-within/retry-enrich:block">
                                    The report was not generated. Run enrichment again to rebuild it.
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div className="space-y-2">
                            {/* Outreach is active when the team carries mod_sales —
                                the module that owns the Outreach engine. Without it
                                the button stays locked and opens the request modal. */}
                            <button
                                className={hasSalesModule
                                  ? "flex min-h-9 w-full items-center justify-center gap-2 rounded-lg border border-white/10 bg-gradient-to-r from-[#15FBFF] to-[#4FCDCF] px-3 py-2 font-bold text-[#0a0a0a] shadow-lg transition-all duration-300 hover:scale-[1.02] hover:from-[#4FCDCF] hover:to-[#15FBFF] hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
                                  : "flex min-h-9 w-full items-center justify-center gap-2 rounded-lg border border-amber-400/30 bg-[linear-gradient(135deg,rgba(251,191,36,0.14),rgba(10,10,10,0.92))] px-3 py-2 font-bold text-amber-100 shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-amber-300/50"}
                                onClick={hasSalesModule ? handleOutreach : () => setShowUnlockOutreachModal(true)}
                                disabled={hasSalesModule ? (isOutreaching || isCampaignOutreachBlocked) : false}
                                title={hasSalesModule ? (isCampaignOutreachBlocked ? 'Outreach is blocked for this campaign' : undefined) : 'Outreach requires the Sales Intelligence module — request activation from our team.'}
                            >
                                {hasSalesModule ? (
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                  </svg>
                                ) : (
                                  <Icon icon="solar:lock-keyhole-bold" className="h-4 w-4" />
                                )}
                                <span className="text-[11px] uppercase tracking-wider">
                                  {hasSalesModule ? (isCampaignOutreachBlocked ? 'Outreach Blocked' : 'Start Outreach') : 'Unlock Outreach'}
                                </span>
                            </button>
                        </div>
                    );
                } else if (statusLabel === 'OUTREACHING') {
                    // OUTREACHING status - blue color, disabled, with spinner
                    return (
                        <button
                            className="flex min-h-9 w-full cursor-not-allowed items-center justify-center gap-2 rounded-lg border border-[#3B82F6]/30 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] px-3 py-2 font-bold text-white shadow-lg shadow-blue-500/20"
                            disabled
                        >
                            <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="text-[11px] uppercase tracking-wider">Outreaching...</span>
                        </button>
                    );
                } else {
                    // For other statuses (OUTREACHED, etc.), show a disabled state button
                    return (
                        <button
                            className="flex min-h-9 w-full cursor-not-allowed items-center justify-center gap-2 rounded-lg border border-white/5 bg-[#1a1a1a] px-3 py-2 font-bold text-gray-500"
                            disabled
                        >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                            </svg>
                            <span className="text-[11px] uppercase tracking-wider">{statusLabel}</span>
                        </button>
                    );
                }
            })()}
        </div>

        <SupportRequestModal
          isOpen={showUnlockOutreachModal}
          onClose={() => setShowUnlockOutreachModal(false)}
          userEmail={currentUser?.email}
          userName={currentUser?.displayName}
          impersonatedEmail={impersonatedEmail}
          clientInfo={clientInfo}
          mode="unlock_outreach"
        />

        {!previewMode && canDownloadReport && (
            <button
                className="flex min-h-9 w-full items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 font-semibold text-white transition-all duration-300 hover:scale-[1.02] hover:border-white/20 hover:bg-white/[0.08]"
                onClick={handleDownloadReport}
                title="Download generated lead report"
            >
                <Icon icon="solar:download-linear" className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="text-[11px] uppercase tracking-wider">Download Report</span>
            </button>
        )}

        {!previewMode && shouldShowRetryEnrich && (
            <div className="rounded-xl border border-amber-400/18 bg-amber-400/8 px-3 py-2.5 text-left">
                <div className="flex items-start gap-2">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-amber-300/18 bg-amber-300/10 text-amber-100">
                        <Icon icon="solar:danger-triangle-bold" className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                        <div className="text-[9px] font-semibold uppercase tracking-[0.14em] text-amber-200/90">Report Missing</div>
                        <p className="mt-0.5 text-[11px] leading-4 text-amber-50/80">
                            This lead reached enriched status, but the PDF report was never created. Run enrichment again to rebuild the report cleanly.
                        </p>
                    </div>
                </div>
            </div>
        )}

        {!previewMode && (() => {
          const isDiscarded = getNormalizedStatusKey(optimisticStatus || data.status) === 'discarded';

          const handleDiscardToggle = async () => {
            if (!effectiveUserEmail) return;
            setIsDiscarding(true);
            try {
              await n8nService.updateCampaign({
                campaign_id: data.campaignId.toString(),
                lvl2_id: data.id.toString(),
                lead_status: isDiscarded ? 'revert_discarded' : 'discarded',
                icp_score: data.icpScore,
                client_email: effectiveUserEmail,
                client_id: getEffectiveUserId(impersonatedEmail, currentUser?.uid, clientProfile),
                client_name: currentUser?.displayName || undefined,
              });

              setOptimisticStatus(isDiscarded ? 'new_lead' : 'discarded');
              showSuccessToast(isDiscarded ? 'Lead restored' : 'Lead discarded');

              queryClient.invalidateQueries({ queryKey: queryKeys.leads.byCampaign(data.campaignId.toString(), effectiveUserEmail) });
            } catch (err) {
              console.error('❌ Failed to toggle discard:', err);
              showErrorToast(isDiscarded ? 'Failed to restore lead' : 'Failed to discard lead');
            } finally {
              setIsDiscarding(false);
            }
          };

          return (
            <button
              onClick={handleDiscardToggle}
              disabled={isDiscarding}
              className={`flex min-h-9 w-full items-center justify-center gap-2 rounded-lg border px-3 py-2 transition-all disabled:opacity-50 ${
                isDiscarded
                  ? 'border-emerald-500/20 bg-emerald-500/8 text-emerald-400 hover:border-emerald-400/40 hover:bg-emerald-500/15 hover:text-emerald-300'
                  : 'border-white/8 bg-white/[0.03] text-gray-500 hover:border-red-400/25 hover:bg-red-500/8 hover:text-red-400'
              }`}
            >
              <Icon icon={isDiscarded ? 'solar:undo-left-round-linear' : 'solar:close-circle-linear'} className="h-4 w-4" />
              <span className="text-[10px] font-medium uppercase tracking-wider">
                {isDiscarding ? 'Processing...' : isDiscarded ? 'Restore Lead' : 'Discard Lead'}
              </span>
            </button>
          );
        })()}

        {/* Profile Insights - Compact */}
        <div>
            <div className="mb-2 flex items-center justify-between px-1">
                <h3 className="text-[10px] font-black uppercase tracking-[0.18em] text-[#7E88A8]">Key Insights</h3>
                <div className="ml-2 h-px flex-1 bg-gradient-to-r from-white/10 to-transparent"></div>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
                {insightCards.map((insight, idx) => (
                    <div key={idx} className="group relative">
                        <div className="relative flex min-h-11 items-center gap-2 rounded-lg bg-white/[0.03] px-2 py-1.5 transition-all duration-300 hover:bg-white/[0.05]">
                            <div className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-white/[0.04] ${insight.color}`}>
                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                    {insight.icon}
                                </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="truncate text-[8px] font-black uppercase tracking-[0.12em] text-[#7E88A8]">{insight.label}</div>
                                <div className="truncate text-[11px] font-semibold text-gray-200" title={String(insight.value ?? '')}>{insight.value}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>

        {/* Skills & Products - Compact */}
        <div>
            <div className="mb-2 flex items-center justify-between px-1">
                <h3 className="text-[10px] font-black uppercase tracking-[0.18em] text-[#7E88A8]">Expertise</h3>
                <div className="ml-2 h-px flex-1 bg-gradient-to-r from-white/10 to-transparent"></div>
            </div>
            <div className="rounded-xl border border-white/8 bg-white/[0.03] p-2.5">
                <div className="space-y-2.5">
                    <div>
                        <div className="mb-1.5 flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.16em] text-[#7E88A8]">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                            </svg>
                            Skills
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {data.skills && data.skills.length > 0 ? (
                                data.skills.map((skill, idx) => (
                                    <span key={idx} className="inline-flex cursor-default items-center rounded-md bg-white/[0.06] px-2 py-1 text-[9px] font-semibold text-gray-200">
                                        {skill}
                                    </span>
                                ))
                            ) : (
                                <span className="inline-flex cursor-default items-center rounded-md bg-white/[0.03] px-2 py-1 text-[9px] font-semibold italic text-gray-500">
                                    No data
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="h-px bg-gradient-to-r from-white/10 via-white/5 to-transparent"></div>

                    <div>
                        <div className="mb-1.5 flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.16em] text-[#7E88A8]">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                            </svg>
                            Products
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {data.keyProductsServices && data.keyProductsServices.length > 0 ? (
                                data.keyProductsServices.map((product, idx) => (
                                    <span key={idx} className="inline-flex cursor-default items-center rounded-md bg-white/[0.06] px-2 py-1 text-[9px] font-semibold text-gray-200">
                                        {product}
                                    </span>
                                ))
                            ) : (
                                <span className="inline-flex cursor-default items-center rounded-md bg-white/[0.03] px-2 py-1 text-[9px] font-semibold italic text-gray-500">
                                    No data
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </div>
    </div>
  );
};
